import type { RouteLocationRaw } from 'vue-router'

import { useUserStore } from '@/stores/user'

/**
 * Where to land a provider user once we know which provider they're acting as.
 * Shared between dashboard/interstitial.vue (single-provider users) and
 * dashboard/select-provider.vue (multi-provider users, after they pick one) so the
 * "what does this provider have access to" decision only lives in one place.
 *
 * Returns null when there is nothing the user can open — callers should show a
 * "needs account setup" state in that case, same as interstitial.vue does today.
 */
export function resolveProviderLandingRoute(providerUuid: string): RouteLocationRaw | null {
  const user = useUserStore()
  const abilities = user.data?.abilities ?? []
  const hasVerification = abilities.some((a: any) => a.subject === 'VerificationSession')
  const hasReports = abilities.some((a: any) => a.action === 'read' && a.subject === 'ProviderReport')

  // TEMPORARY: single-product placeholder default landing for non-admin users.
  // Replace with real multi-product homepage logic once designed (separate ticket).
  if (hasVerification)
    return { name: 'iv-lookup', params: { provider_uuid: providerUuid } }

  // Co-pay is never surfaced to provider users (Vector-admin only) and SSDI has no provider
  // page, so an admin at a hospital without IVD (a finance contact invited for the monthly
  // reports) lands on Reports & Invoices (VH-3637). Hospital admins hold `read ProviderReport`;
  // the page itself is released to them by VH-3551.
  if (hasReports)
    return { name: 'provider-reports', params: { provider_uuid: providerUuid } }

  return null
}
